import { Modal, useModals } from 'almanac-ui/Modal'
import { useId } from '@react-aria/utils'
import { useUpdateBrandPlanSchedule } from 'hooks/queries/schedules/useUpdateBrandPlanSchedule'
import { Form, Formik, useField } from 'formik'
import { EditChannelsModalProps, EditChannelsModalWrapperProps } from './Types'
import Styles from 'screens/Campaign/Overview/EditChannelsModal/EditChannelsModal.module.scss'
import { Blogpost, InstagramBrand } from 'blaze-ui/icons/24'
import { FacebookBrand } from 'blaze-ui/icons/35'
import { LinkedInBrand } from 'blaze-ui/icons/32'
import { TikTokBrand, TwitterBrand, YouTubeBrand } from 'blaze-ui/icons'
import { CountsSection } from 'screens/Campaign/Overview/EditChannelsModal/CountsSection'
import { HighDetailedMail, HighDetailedSocial } from 'blaze-ui/icons/40'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { ContentsCount } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/ContentsCount'
import cx from 'classnames'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { FormikPostDaysRadioGroup } from 'screens/VirtualMarketerOnboarding/CustomizeSchedule/PostDaysRadioGroup'
import { validationSchema } from 'screens/Campaign/Overview/EditChannelsModal/validationSchema'
import { CrosspostFrequency } from 'screens/Campaign/Overview/EditChannelsModal/CrosspostFrequency'
import { CrosspostFrequency as CrosspostFrequencyType } from 'types'
import { Crossposts } from 'screens/Campaign/Overview/EditChannelsModal/Crossposts'
import {
  mergeCrosspostDefaults,
  mergeEmailDefaults,
  mergeFacebookDefaults,
  mergeGoogleBusinessProfileDefaults,
  mergeInstagramDefaults,
  mergeLinkedInDefaults,
  mergeSeoDefaults,
  mergeTiktokDefaults,
  mergeTwitterDefaults,
  mergeVideoCrosspostDefaults,
  mergeYoutubeDefaults,
} from 'utils/brandPlans/mergeDefaultContents'
export const BlogSection = () => {
  const id = useId()
  return (
    <section className={cx(Styles.section, Styles.blogSection)} role="group" aria-labelledby={id}>
      <header className={Styles.header}>
        <Blogpost size={40} />
        <div className={Styles.heading}>
          <div className={Styles.headingText}>
            <Heading level={4} id={id}>
              Blogs
            </Heading>
            <Text variant="secondary">/ week</Text>
          </div>
          <Text variant="secondary">Own search results</Text>
        </div>
      </header>
      <ContentsCount
        name="seo_contents"
        buttonVariant="tertiary"
        weekLabel={null}
        customLabel={null}
        aria-label="Blog Posts"
      />
    </section>
  )
}

export const EmailSection = () => {
  const id = useId()
  return (
    <section className={cx(Styles.section, Styles.emailSection)} role="group" aria-labelledby={id}>
      <header className={Styles.header}>
        <HighDetailedMail size={40} />
        <div className={Styles.heading}>
          <div className={Styles.headingText}>
            <Heading level={4} id={id}>
              Emails
            </Heading>
            <Text variant="secondary">/ week</Text>
          </div>
          <Text variant="secondary">Stay top-of-mind</Text>
        </div>
      </header>
      <ContentsCount
        name="email_contents"
        buttonVariant="tertiary"
        weekLabel={null}
        customLabel={null}
        aria-label="Email Newsletters"
      />
    </section>
  )
}

export const SocialMediaSection = () => {
  const [{ value: crosspostFrequency }] = useField<CrosspostFrequencyType>('crosspost_frequency')
  return (
    <section className={Styles.section}>
      <header className={Styles.header}>
        <HighDetailedSocial size={40} />
        <div className={Styles.heading}>
          <Heading level={4}>Social Media</Heading>
          <Text variant="secondary">Build your audience</Text>
        </div>
      </header>
      <CrosspostFrequency />
      {crosspostFrequency === 'never' && (
        <div className={Styles.channels}>
          <Heading level={4}>Which channels do you want to post to?</Heading>
          <div className={Styles.socialChannels}>
            <CountsSection icon={InstagramBrand} label="Instagram" name="instagram_contents" />
            <CountsSection icon={FacebookBrand} label="Facebook" name="facebook_contents" />
            <CountsSection icon={LinkedInBrand} label="LinkedIn" name="linkedin_contents" />
            <CountsSection icon={YouTubeBrand} label="Youtube" name="youtube_contents" />
            <CountsSection icon={TwitterBrand} label="X / Twitter" name="twitter_contents" />
            <CountsSection icon={TikTokBrand} label="TikTok" name="tiktok_contents" />
          </div>
        </div>
      )}
      {crosspostFrequency === 'always' && <Crossposts />}
      <DaysToPost />
    </section>
  )
}

const DaysToPost = () => {
  const id = useId()

  return (
    <div className={Styles.daysToPost}>
      <Heading level={4} id={id}>
        Days to post (per channel)
      </Heading>
      <FormikPostDaysRadioGroup name="week_days" aria-labelledby={id} chipSize="lg" className={Styles.radioGroup} />
    </div>
  )
}

export const FormSection = () => {
  return (
    <Form className={Styles.form}>
      <SocialMediaSection />
      <BlogSection />
      <EmailSection />
    </Form>
  )
}

export const EditChannelsModal = ({ campaign, onSubmit }: EditChannelsModalProps) => {
  const { closeModal } = useModals()
  const id = useId()

  const {
    instagram_contents = [],
    facebook_contents = [],
    linkedin_contents = [],
    twitter_contents = [],
    tiktok_contents = [],
    seo_contents = [],
    email_contents = [],
    youtube_contents = [],
    crosspost_contents = [],
    crosspost_frequency = 'never',
    video_crosspost_contents = [],
    google_my_business_contents = [],
    week_days,
    tactics = [],
  } = campaign

  return (
    <Formik
      validateOnMount
      validationSchema={validationSchema}
      initialValues={{
        instagram_contents: mergeInstagramDefaults(instagram_contents),
        facebook_contents: mergeFacebookDefaults(facebook_contents),
        linkedin_contents: mergeLinkedInDefaults(linkedin_contents),
        twitter_contents: mergeTwitterDefaults(twitter_contents),
        tiktok_contents: mergeTiktokDefaults(tiktok_contents),
        seo_contents: mergeSeoDefaults(seo_contents),
        email_contents: mergeEmailDefaults(email_contents),
        youtube_contents: mergeYoutubeDefaults(youtube_contents),
        crosspost_contents: mergeCrosspostDefaults(crosspost_contents),
        crosspost_frequency: ['always', 'never'].includes(crosspost_frequency) ? crosspost_frequency : 'always',
        video_crosspost_contents: mergeVideoCrosspostDefaults(video_crosspost_contents),
        google_my_business_contents: mergeGoogleBusinessProfileDefaults(google_my_business_contents),
        week_days,
        tactics,
      }}
      onSubmit={onSubmit}
    >
      {({ submitForm, isValid }) => {
        return (
          <Modal.Root size="sm" blaze aria-labelledby={id}>
            <Modal.Header title="Select Channels for your campaign" />
            <Modal.Content>
              <FormSection />
            </Modal.Content>
            <Modal.Footer>
              <Modal.FooterContent slot="left">
                <Modal.FooterButton onPress={() => closeModal()} size="lg" variant="tertiary">
                  Cancel
                </Modal.FooterButton>
              </Modal.FooterContent>
              <Modal.FooterContent slot="right">
                <Modal.FooterButton isDisabled={!isValid} onPress={() => submitForm()} size="lg" variant="primary">
                  Save
                </Modal.FooterButton>
              </Modal.FooterContent>
            </Modal.Footer>
          </Modal.Root>
        )
      }}
    </Formik>
  )
}

export const EditChannelsModalWrapper = ({ campaign }: EditChannelsModalWrapperProps) => {
  const { closeModal } = useModals()

  const { mutate } = useUpdateBrandPlanSchedule(
    { id: campaign.id },
    {
      onSuccess: () => closeModal(),
      onError: () => {
        showErrorMessageToast('There was an error updating the campaign. Please try again.')
      },
    },
  )

  return <EditChannelsModal onSubmit={mutate} campaign={campaign} />
}
